import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, LoaderIcon } from '../components/Icons';
import { API_BASE_URL, AUTH_TOKEN_KEY } from '../services/api';
import './Auth.css';

const ConfirmEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const confirmEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setErrorMessage('Token de confirmação não encontrado');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/auth/confirm-email?token=${token}`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Erro ao confirmar e-mail');
        }

        const data = await response.json();

        // Store the access token (app uses 'token' as the key)
        localStorage.setItem(AUTH_TOKEN_KEY, data.access_token);

        setStatus('success');

        // Redirect to upload after 2 seconds
        setTimeout(() => {
          window.location.href = '/upload';
        }, 2000);
      } catch (error: any) {
        setStatus('error');
        setErrorMessage(error.message || 'Erro ao confirmar e-mail');
      }
    };

    confirmEmail();
  }, [searchParams]);

  return (
    <div className="auth-page">
      {/* Header */}
      <header className="auth-header">
        <div className="auth-header-content">
          <div className="auth-logo" onClick={() => navigate('/')}>
            <img src="/logo_transp.png" alt="ContratoSeguro" />
          </div>
          <div className="auth-header-actions">
            <button onClick={() => navigate('/')} className="btn-text">
              Voltar para o início
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="auth-main">
        <div className="auth-container">
          <div className="confirmation-pending-container">
            <div className="confirmation-card">
              {status === 'loading' && (
                <>
                  <div className="confirmation-icon loading">
                    <LoaderIcon size={64} />
                  </div>
                  <h1>Confirmando seu e-mail...</h1>
                  <p className="confirmation-message">
                    Aguarde enquanto verificamos sua confirmação
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className="confirmation-icon success">
                    <CheckCircleIcon size={64} />
                  </div>
                  <h1>E-mail confirmado com sucesso!</h1>
                  <p className="confirmation-message">
                    Bem-vindo ao ContratoSeguro! Você será redirecionado em instantes...
                  </p>
                  <div className="confirmation-success-message">
                    <p>Sua conta está ativa e pronta para uso.</p>
                    <p>Você receberá um e-mail de boas-vindas em breve.</p>
                  </div>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className="confirmation-icon error">
                    <XCircleIcon size={64} />
                  </div>
                  <h1>Erro ao confirmar e-mail</h1>
                  <p className="confirmation-message error">
                    {errorMessage}
                  </p>
                  <div className="confirmation-help">
                    <p className="help-text">O que pode ter acontecido?</p>
                    <ul>
                      <li>O link de confirmação expirou (válido por 24 horas)</li>
                      <li>O link já foi utilizado anteriormente</li>
                      <li>O link está incompleto ou foi modificado</li>
                    </ul>
                  </div>
                  <div className="confirmation-actions">
                    <button onClick={() => navigate('/signup')} className="btn-primary">
                      Criar nova conta
                    </button>
                    <button onClick={() => navigate('/login')} className="btn-secondary">
                      Ir para o login
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="auth-footer-section">
        <div className="auth-footer-content">
          <div className="footer-left">
            <img src="/logo_transp.png" alt="ContratoSeguro" className="footer-logo-small" />
            <div className="footer-copy">
              <p className="footer-brand">ContratoSeguro</p>
              <p className="footer-tagline">Clareza para quem assina.</p>
            </div>
          </div>
          <div className="footer-center">
            <a href="/termos-de-uso">Termos de Uso</a>
            <a href="/politica-de-privacidade">Política de Privacidade</a>
            <a href="/politica-de-cookies">Política de Cookies</a>
            <a href="/aviso-legal">Aviso Legal</a>
          </div>
          <div className="footer-right">
            <p className="footer-heading">Contato</p>
            <p className="footer-contact">suporte@contratoseguro.com.br</p>
            <p className="footer-heading">LGPD</p>
            <p className="footer-contact">privacidade@contratoseguro.com.br</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConfirmEmail;
